import { OptionallyVisible } from 'components';
import React, { useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

import { Button } from 'components/buttons';
import { Panel } from 'components/containers';
import { DataTable } from 'components/dataTable';
import IconAdd from 'components/icons/add';
import LoadingIndicator from 'components/loadingIndicator';

import { CreateApiKeyResponse } from 'types/apiKeys';

import { COLUMNS, LABELS } from './apiKeys.keys';
import { ApiKeyModal } from './components/apiKeyModal';
import { customCellComponentFactory } from './components/cells';
import { DeleteApiKeyModal } from './components/deleteApiKeyModal';
import { ApiKeyFormPayload } from './container';
import { ActionPanel, ButtonContent, ButtonLabel, EmptyState, InfoMessage } from './styles';
import { ApiKeyTableRow } from './types';

interface APIKeysSectionProps extends WithTranslation {
  canManage: boolean;
  canManageActions: boolean;
  kybApproved: boolean;
  kybMessage?: string;
  rows: ApiKeyTableRow[];
  isLoading: boolean;
  createApiKey: {
    onCreate: (payload: ApiKeyFormPayload) => Promise<{ success: boolean; createdKey?: CreateApiKeyResponse }>;
    isSubmitting: boolean;
    error?: Record<string, string>;
    reset: () => void;
  };
  updateApiKey: {
    onUpdate: (apiKeyUuid: string, payload: ApiKeyFormPayload) => Promise<boolean>;
    isSubmitting: boolean;
    error?: Record<string, string>;
    reset: () => void;
  };
  deleteApiKey: {
    onDelete: (apiKeyUuid: string) => Promise<boolean>;
    isSubmitting: boolean;
  };
}

enum ApiKeyActionModalType {
  Form,
  Delete,
}

export const APIKeysSectionComponent = ({
  canManage,
  canManageActions,
  kybApproved,
  kybMessage,
  rows,
  isLoading,
  createApiKey,
  updateApiKey,
  deleteApiKey,
  t,
}: APIKeysSectionProps) => {
  const [selectedRow, setSelectedRow] = useState<ApiKeyTableRow | null>(null);
  const [selectedModal, setSelectedModal] = useState<ApiKeyActionModalType | null>(null);

  if (!canManage) {
    return (
      <Panel label={t(LABELS.PANEL_TITLE)}>
        <InfoMessage>{t(LABELS.ACCESS_DENIED)}</InfoMessage>
      </Panel>
    );
  }

  if (isLoading) {
    return (
      <Panel label={t(LABELS.PANEL_TITLE)}>
        <LoadingIndicator padded />
      </Panel>
    );
  }

  if (!kybApproved) {
    return (
      <Panel label={t(LABELS.PANEL_TITLE)}>
        <InfoMessage>{kybMessage}</InfoMessage>
      </Panel>
    );
  }

  const openCreateModal = () => {
    setSelectedRow(null);
    setSelectedModal(ApiKeyActionModalType.Form);
  };

  const findRow = (uuid: string) => rows.find((row) => row.uuid === uuid) || null;

  const openEditModal = (uuid: string) => {
    setSelectedRow(findRow(uuid));
    setSelectedModal(ApiKeyActionModalType.Form);
  };

  const openDeleteModal = (uuid: string) => {
    setSelectedRow(findRow(uuid));
    setSelectedModal(ApiKeyActionModalType.Delete);
  };

  const closeModal = () => {
    setSelectedRow(null);
    setSelectedModal(null);
  };

  const ApiKeysTableCell = customCellComponentFactory({
    onEdit: openEditModal,
    onDelete: openDeleteModal,
    canManageActions,
  });

  const isFormModalOpen = selectedModal === ApiKeyActionModalType.Form;
  const isEditingSelectedRow = Boolean(selectedRow) && isFormModalOpen;

  return (
    <Panel label={t(LABELS.PANEL_TITLE)}>
      <OptionallyVisible visible={canManageActions}>
        <ActionPanel>
          <Button flat onClick={openCreateModal}>
            <ButtonContent>
              <IconAdd />
              <ButtonLabel>{t(LABELS.CREATE_BUTTON)}</ButtonLabel>
            </ButtonContent>
          </Button>
        </ActionPanel>
      </OptionallyVisible>

      <OptionallyVisible visible={rows.length > 0}>
        <DataTable columns={COLUMNS} rows={rows} CellComponent={ApiKeysTableCell} />
      </OptionallyVisible>

      <OptionallyVisible visible={rows.length === 0}>
        <EmptyState>{t(LABELS.EMPTY_STATE)}</EmptyState>
      </OptionallyVisible>

      <ApiKeyModal
        open={isFormModalOpen}
        onClose={closeModal}
        editingKey={selectedRow}
        onCreate={createApiKey.onCreate}
        onUpdate={updateApiKey.onUpdate}
        isSubmitting={isEditingSelectedRow ? updateApiKey.isSubmitting : createApiKey.isSubmitting}
        error={isEditingSelectedRow ? updateApiKey.error : createApiKey.error}
        reset={isEditingSelectedRow ? updateApiKey.reset : createApiKey.reset}
      />

      <DeleteApiKeyModal
        open={selectedModal === ApiKeyActionModalType.Delete}
        onClose={closeModal}
        apiKey={selectedRow}
        onDelete={deleteApiKey.onDelete}
        isSubmitting={deleteApiKey.isSubmitting}
      />
    </Panel>
  );
};

export const APIKeysSection = withTranslation()(APIKeysSectionComponent);
